import type { ArtworkPruefung } from "../../../../api/artworkPruefungen.ts";
import type { AnzeigePruefung } from "./fassung.ts";

/**
 * Das Abzeichen am Artwork im Reiter Dokumente (Artwork-Abgleich, Spec „Anzeige“): ein Knopf mit dem Gesamtergebnis, ein
 * Klick öffnet die Einzelheiten (`ArtworkPruefungEinzelheiten`). Die Texte sind festgelegt: „Prüfung läuft“, „Passt“,
 * „1 Warnung“ / „N Warnungen“ (bei gesehener Warnung mit „ · gesehen“), „Bitte selbst prüfen“, „Nicht prüfbar“,
 * „Nicht geprüft“, und „Stand veraltet“ (`pruefungDerFassung`: die gezeigte Fassung ist überholt; neutral wie „Nicht geprüft“ und
 * KEIN Knopf, denn zu diesem Stand gibt es keine Einzelheiten). Der Zustand steht im Text, nicht nur in der Farbe
 * (`data-zustand` gehört dem Stylesheet). Bei gesehener Warnung nennt der Hilfetext (`title`) Name und Tag: „Warnung · gesehen von
 * Damian am 24.10.“ (Spec, Abschnitt „Anzeige“); der Text des Abzeichens bleibt kurz.
 */

/** Wofür das Abzeichen steht; die Farbe im Stylesheet hängt an `data-zustand`. */
type AbzeichenArt =
  | "nicht-geprueft"
  | "veraltet"
  | "laeuft"
  | "passt"
  | "warnung"
  | "selbst-pruefen"
  | "nicht-pruefbar";

/**
 * Ein kurzer deutscher Satz je Grund von „nicht prüfbar“, ohne Fachjargon. Unbekannte Gründe (ein späterer Server) fallen auf
 * `GRUND_UNBEKANNT` zurück.
 */
const GRUND_TEXT: ReadonlyMap<string, string> = new Map([
  ["format", "Dateiformat wird nicht gelesen"],
  ["defekt", "Datei ist defekt oder verschlüsselt"],
  ["kein_barcode", "Kein Barcode gefunden"],
  ["position_fehlt", "Position in JTL nicht mehr vorhanden"],
  ["ohne_artikel", "Position hat in JTL keinen Artikel"],
  ["grenze", "Datei ist zu groß oder zu aufwendig für die Prüfung"],
  ["technischer_fehler", "Technischer Fehler bei der Prüfung"],
]);

const GRUND_UNBEKANNT = "Prüfung war nicht möglich";

export function grundText(grund: string | null): string {
  return (grund === null ? undefined : GRUND_TEXT.get(grund)) ?? GRUND_UNBEKANNT;
}

/** „TT.MM.JJJJ HH:MM“ in der Ortszeit des Browsers; ein nicht lesbarer Wert bleibt, wie er ist. Auch die Einzelheiten zeigen so. */
export function datumZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return iso;
  const zwei = (zahl: number): string => String(zahl).padStart(2, "0");
  return `${zwei(zeitpunkt.getDate())}.${zwei(zeitpunkt.getMonth() + 1)}.${zeitpunkt.getFullYear()} ${zwei(zeitpunkt.getHours())}:${zwei(zeitpunkt.getMinutes())}`;
}

/** Der Hilfetext einer gesehenen Warnung: „Warnung · gesehen von <Name> am <TT.MM.>“, ohne Namen „Warnung · gesehen am <TT.MM.>“. */
function gesehenText(von: string | null, am: string): string {
  const datum = datumZeit(am);
  return `Warnung · gesehen ${von ? `von ${von} ` : ""}am ${datum === am ? am : datum.slice(0, 6)}`;
}

/** Die Zahl der Prüfpunkte mit dem Wert „warnung“ - die Zahl im Abzeichen. */
function warnungsZahl(pruefung: ArtworkPruefung | null | undefined): number {
  return pruefung?.ergebnis?.punkte.filter((punkt) => punkt.wert === "warnung").length ?? 0;
}

/** Eine fertige Prüfung mit Gesamtergebnis „Warnung“, die noch niemand als gesehen markiert hat (der Fall fürs Board). */
export function hatOffeneWarnung(pruefung: ArtworkPruefung | null | undefined): boolean {
  return pruefung?.zustand === "fertig" && pruefung.gesamt === "warnung" && !pruefung.gesehenAm;
}

/**
 * Ohne Eintrag (der Server kennt das Artwork noch nicht, oder das Laden schlug fehl) gilt „nicht geprüft“; ebenso eine
 * fertige Prüfung ohne Gesamtergebnis (ein inzwischen gelöschter Link, den der Worker ohne Ergebnis abschloss).
 */
export function abzeichenArt(pruefung: AnzeigePruefung | null | undefined): AbzeichenArt {
  if (!pruefung) return "nicht-geprueft";
  if (pruefung.veraltet) return "veraltet";
  if (pruefung.zustand === "laeuft") return "laeuft";
  if (pruefung.zustand !== "fertig") return "nicht-geprueft";
  switch (pruefung.gesamt) {
    case "passt": return "passt";
    case "warnung": return "warnung";
    case "selbst_pruefen": return "selbst-pruefen";
    case "nicht_pruefbar": return "nicht-pruefbar";
    default: return "nicht-geprueft";
  }
}

export function abzeichenText(pruefung: AnzeigePruefung | null | undefined): string {
  switch (abzeichenArt(pruefung)) {
    case "veraltet": return "Stand veraltet";
    case "laeuft": return "Prüfung läuft";
    case "passt": return "Passt";
    case "warnung": {
      // Eine Warnung ohne zählbaren Punkt (kann der Server nicht liefern) zeigt trotzdem mindestens „1 Warnung“.
      const zahl = Math.max(1, warnungsZahl(pruefung));
      const text = zahl === 1 ? "1 Warnung" : `${zahl} Warnungen`;
      return pruefung?.gesehenAm ? `${text} · gesehen` : text;
    }
    case "selbst-pruefen": return "Bitte selbst prüfen";
    case "nicht-pruefbar": return "Nicht prüfbar";
    case "nicht-geprueft": return "Nicht geprüft";
  }
}

/** Die Zeile, in der ein Abzeichen steht (Reiter: `.doc-row`, Zusammenfassung: `li`). Der Aufrufer merkt sie sich beim Öffnen der Einzelheiten. */
export const zeileDesAbzeichens = (ausloeser: HTMLElement): HTMLElement | null => ausloeser.closest<HTMLElement>("li, .doc-row");

/**
 * Gibt den Fokus nach dem Schließen der Einzelheiten zurück: an den Auslöser. Schließen sie, weil der Stand „veraltet“ wurde, ist aus
 * dem Knopf eine Beschriftung geworden und der Auslöser nicht mehr im Dokument; dann geht der Fokus an das neue Abzeichen derselben
 * Zeile (`tabIndex={-1}`, siehe oben), damit er nicht auf `body` fällt.
 */
export function fokusZumAbzeichen(ausloeser: HTMLElement | null, zeile: HTMLElement | null): void {
  if (ausloeser?.isConnected) ausloeser.focus();
  else zeile?.querySelector<HTMLElement>(".artwork-pruefung-abzeichen")?.focus();
}

interface ArtworkPruefungAbzeichenProps {
  pruefung: AnzeigePruefung | null | undefined;
  /** Der Auslöser wird mitgegeben, damit der Aufrufer den Fokus nach dem Schließen der Einzelheiten dorthin zurückgeben kann. */
  onOeffnen: (ausloeser: HTMLElement) => void;
}

export function ArtworkPruefungAbzeichen({ pruefung, onOeffnen }: ArtworkPruefungAbzeichenProps) {
  const art = abzeichenArt(pruefung);
  if (art === "veraltet") {
    // Eine Beschriftung, kein Knopf: es gibt keine Einzelheiten zu diesem Stand, die Fassung des Aufrufers ist überholt.
    return (
      <span
        className="artwork-pruefung-abzeichen"
        data-zustand={art}
        tabIndex={-1}
        title="Das Artwork wurde inzwischen geändert. Bestellung neu öffnen, um den aktuellen Prüfstand zu sehen."
      >{abzeichenText(pruefung)}</span>
    );
  }
  const hilfe = art === "nicht-pruefbar"
    ? grundText(pruefung?.grund ?? null)
    : art === "warnung" && pruefung?.gesehenAm
      ? gesehenText(pruefung.gesehenVon, pruefung.gesehenAm)
      : "Einzelheiten der Artwork-Prüfung anzeigen";
  return (
    <button
      type="button"
      className="artwork-pruefung-abzeichen"
      data-zustand={art}
      data-gesehen={art === "warnung" && pruefung?.gesehenAm ? "" : undefined}
      title={hilfe}
      onClick={(event) => onOeffnen(event.currentTarget)}
    >{abzeichenText(pruefung)}</button>
  );
}
